import {
  AgDataGrid,
  AgDataGridProps,
  PaginationModel,
} from '@australiangreens/ag-internal-components';
import { EXPANDED_TABLE_PROPERTIES, TABLE_LINK_STYLE } from '@/constants';
import { Search, SearchDbRecordResult } from '@/types';
import { CollapsibleText } from '@components/CollapsibleText';
import {
  GridActionsCellItem,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { usePrefetchSearchTree } from '@pages/AdvancedSearchListCreatorPage/JsonTreeBuilder';
import moment from 'moment';
import { Link } from 'react-router';

type SavedSearchesTableProps = {
  data: SearchDbRecordResult[];
  isLoading: boolean;
  paginationModel: PaginationModel;
  onPaginationModelChange?: (model: PaginationModel) => void;
  sortModel?: GridSortModel | undefined;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails
  ) => void;
  onSortModelChange?: ((model: GridSortModel, details: GridCallbackDetails) => void) | undefined;
  activateDelete: (search: Search) => void;
  activateEdit: (search: Search) => void;
} & Partial<AgDataGridProps>;

const SavedSearchesTable = ({
  data,
  isLoading,
  paginationModel,
  onPaginationModelChange = undefined,
  rowCount = undefined,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  activateDelete,
  activateEdit,
  sortModel = undefined,
  onSortModelChange = undefined,
}: SavedSearchesTableProps) => {
  const prefetchSearchTree = usePrefetchSearchTree();

  const COLUMN_DEFS: GridColDef<Search, string, string>[] = [
    {
      headerName: 'Search name',
      field: 'name',
      align: 'left',
      flex: 28,
      editable: false,
      renderCell: (params) =>
        params.row.searchType === 'Quick' ? (
          <Link
            style={TABLE_LINK_STYLE}
            title={params.row.name}
            to={`/quicksearch/${params.row.id}`}
            state={{
              name: params.row.name,
              description: params.row.description,
              searchJson: params.row.searchJson,
              searchId: params.row.id,
            }}
          >
            {params.row.name}
          </Link>
        ) : (
          <Link
            style={TABLE_LINK_STYLE}
            title={params.row.name}
            to={`/advancedsearch/${params.row.id}`}
            state={{
              name: params.row.name,
              description: params.row.name,
              searchJson: params.row.searchJson,
              searchId: params.row.id,
            }}
            onMouseDown={() => {
              const searchTree = JSON.parse(params.row.searchJson);
              prefetchSearchTree(searchTree);
            }}
          >
            {params.row.name}
          </Link>
        ),
    },
    {
      headerName: 'Type',
      field: 'searchType',
      type: 'singleSelect',
      valueOptions: ['Quick', 'Advanced'],
      flex: 8,
    },
    {
      headerName: 'Date created',
      field: 'createdAt',
      align: 'left',
      flex: 10,
      editable: false,
      type: 'date',
      valueFormatter: (value) =>
        value != null && value !== '' ? moment(value).format('DD/MM/YYYY') : '',
    },
    {
      headerName: 'Created by',
      field: 'savedBySub',
      align: 'left',
      flex: 10,
      editable: false,
    },
    {
      headerName: 'Description',
      field: 'description',
      align: 'left',
      renderCell: (params) => <CollapsibleText text={params.row.description} />,
      flex: 46,
      editable: false,
    },
    {
      headerName: 'Id',
      headerAlign: 'left',
      field: 'id',
      align: 'left',
      flex: 10,
      editable: false,
      type: 'number',
      valueFormatter: (value: unknown) =>
        value == null || value === '' || (typeof value === 'number' && Number.isNaN(value))
          ? ''
          : String(value),
    },
    {
      headerName: '',
      field: 'Actions',
      align: 'right',
      type: 'actions',
      sortable: false,
      flex: 3,
      getActions: (params) => [
        <GridActionsCellItem
          label={'Edit'}
          key="Edit"
          showInMenu
          onClick={() => activateEdit(params.row)}
        />,
        <GridActionsCellItem
          label={'Delete'}
          key="Delete"
          showInMenu
          onClick={() => activateDelete(params.row)}
        />,
      ],
    },
  ];

  return (
    <AgDataGrid
      loading={isLoading}
      rows={data}
      columns={COLUMN_DEFS}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      noResultsMessage={'No searches meet your filter criteria'}
      noRowsMessage={'No searches meet your filter criteria'}
      skeletonColumnWidths={COLUMN_DEFS.map((column) =>
        columnVisibilityModel && columnVisibilityModel[column.field] === false
          ? 0
          : (column.flex ?? 0)
      )}
      rowCount={rowCount}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      pageSizeOptions={[5, 10, 25, 50, 100]}
      disableRowSelectionOnClick={true}
      disableColumnFilter={true}
      getCellClassName={(params: GridCellParams) => {
        if (params.field === 'description') {
          return 'collapsible';
        }
        return '';
      }}
      {...EXPANDED_TABLE_PROPERTIES}
    />
  );
};

export default SavedSearchesTable;
